import React, { useState, useEffect } from 'react';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { Spinner } from 'reactstrap';
import {
  PageContainer,
  FormLabel,
  Description,
  QuestionMark,
  CheckIcon,
  ErrorIcon,
  AlertContent,
  AlertText,
  Link,
  Actions,
  FormFeedback,
  Button,
  AlertPopup,
  CharacterLimitAlert,
  CharacterCounter,
  MessageInput,
  FormSpan
} from './style';
import { Row, Form, Col, FormGroup, Input, Popover, PopoverBody, Alert } from 'reactstrap';
import Title from '../../components/Title';
import { useSendUsMessageContext } from '../../contexts/SendUsMessageContext';
import { useForm } from 'react-hook-form';
import { QuestionMark as QuestionMarkIcon } from '../../assets/icons';
import InputWithHide from '../../components/InputWithHide';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { createSafeHtml } from '../../utils/sanitizeHtml';

const SendUsMessage = props => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [lastCase, setLastCase] = useState('');
  const { language } = useLanguageContext();
  const [counter, setCounter] = useState(0); 
  const [limitReached, setLimitReached] = useState(false);
  const characterLimit = 5000;

  useEffect(() => {
    document.title = "DFS | Send Us A Message";
  });

  const {
    title,
    description,
    formLabels,
    contactUslink,
    submitted,
    error,
    questions,
    sendMessage,
    submitting,
  } = useSendUsMessageContext();
  const {
    handleSubmit,
    register,
    errors,
    formState,
    getValues,
    watch,
    setValue,
    clearError,
    setError,
    reset,
  } = useForm({ mode: 'onChange' });

  const toggleTooltip = () => setIsTooltipOpen(!isTooltipOpen);
  const onDismissAlert = () => setIsAlertVisible(false);
  const onSubmitForm = caseInfo => {
    const onSendMessage = data => {
      if (data.caseNumber) setLastCase(data.caseNumber);
      if (!data.error) reset();
      window.scrollTo(0, 0);
    };
    sendMessage(caseInfo, onSendMessage);
    setIsAlertVisible(true);
  };

  const formatPhone = (e, typePhone, required = true) => {
    const value = e.target.value
      ? e.target.value.replace(/\D/g, '').length > 10
        ? e.target.value.slice(0, -1)
        : e.target.value
      : e.target.value;
    const formattedPhone = new AsYouType('US').input(
      value.length === 4 ? value.slice(0, -1) : value,
    );
    const message = formLabels.phoneNumberErrorFormat;
    const phone = parsePhoneNumberFromString(formattedPhone, 'US');
    if (!value && !required) return clearError(typePhone);
    setValue(typePhone, formattedPhone);

    if (phone) {
      if (phone.isValid()) {
        clearError(typePhone);
      } else {
        setError(typePhone, 'invalid', message);
      }
    } else {
      setError(typePhone, 'invalid', message);
    }
  };

  const getTopics = () => {
    if (!questions) return;

    if (language.locale.split('-')[1].toUpperCase() === 'US') {
      return questions
        .find(q => q.subject === getValues().subject)
          ?.questionOptions?.map(({ value, text }) => (
            <option value={value} key={value}>
              {text}
            </option>
          ))
    } else {
      return questions
        .find(q => q.subject === getValues().subject)
          ?.questions?.map(({ key, value }) => (
            <option value={key} key={key}>
              {value}
            </option>
          ))
    }
  };
  
  return (
    <Row className="px-2 mx-1 pb-4 h-100">
      <PageContainer xs="12">
        <Title id="title">{title}</Title>
        <Description
          id="description"
          dangerouslySetInnerHTML={createSafeHtml(description)}
        ></Description>
        {submitted && (
          <Alert
            aria-live="polite"
            role="status"
            color={error ? 'danger' : 'warning'}
            isOpen={isAlertVisible}
            toggle={onDismissAlert}
          >
            <AlertContent>
              {error ? (
                <ErrorIcon className="dds__icons dds__close-x"></ErrorIcon>
              ) : (
                <CheckIcon className="dds__icons dds__check"></CheckIcon>
              )}
              <AlertText
                dangerouslySetInnerHTML={createSafeHtml(
                  error
                    ? formLabels.submitErrorMessage.replace(/{caseNumber}/gim, lastCase)
                    : formLabels.submitSuccessMessage.replace(/{caseNumber}/gim, lastCase)
                )}
              ></AlertText>
            </AlertContent>
          </Alert>
        )}
        <Form className="w-100" onSubmit={handleSubmit(onSubmitForm)}>
          <Row form>
            <Col sm={6}>
              <FormGroup>
                <FormLabel for="firstName">{formLabels.firstName}</FormLabel>
                <Input
                  className="skip-nav-focus"
                  innerRef={register({
                    required: formLabels.firstNameErrorRequired,
                    pattern: {
                      value: /^[a-zA-ZÀ-ú '´`-]+$/,
                      message: formLabels.firstNameErrorPattern,
                    },
                  })}
                  invalid={!!errors.firstName}
                  valid={formState.touched.firstName && !errors.firstName}
                  id="firstName"
                  aria-label={formLabels.firstNameAriaLabel}
                  name="firstName"
                  disabled={submitting}
                  required
                />
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="firstName">{errors.firstName && errors.firstName.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup>
                <FormLabel for="lastName">{formLabels.lastName}</FormLabel>
                <Input
                  innerRef={register({
                    required: formLabels.lastNameErrorRequired,
                    pattern: {
                      value: /^[a-zA-ZÀ-ú '´`-]+$/,
                      message: formLabels.lastNameErrorPattern,
                    },
                  })}
                  id="lastName"
                  name="lastName"
                  invalid={!!errors.lastName}
                  valid={formState.touched.lastName && !errors.lastName}
                  disabled={submitting}
                  required
                  aria-label={formLabels.lastNameAriaLabel}
                />
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="lastName">{errors.lastName && errors.lastName.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup>
                <FormLabel for="emailAddress">{formLabels.emailAddress}</FormLabel>
                <Input
                  innerRef={register({
                    required: formLabels.emailAddressErrorRequired,
                    pattern: {
                      value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/,
                      message: formLabels.emailAddressErrorPattern,
                    },
                  })}
                  invalid={!!errors.emailAddress}
                  valid={formState.touched.emailAddress && !errors.emailAddress}
                  id="emailAddress"
                  name="emailAddress"
                  aria-label={formLabels.emailAddressAriaLabel}
                  disabled={submitting}
                  required
                />
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="emailAddress">{errors.emailAddress && errors.emailAddress.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={6}></Col>
            <Col sm={6}>
              <FormGroup>
                <FormLabel for="accountNumber">{formLabels.accountNumber}</FormLabel>
                <InputWithHide innerRef={register} name="accountNumber" disabled={submitting} />
              </FormGroup>
            </Col>

            <Col sm={6}>
              <FormGroup>
                <FormLabel for="phoneNumber">{formLabels.phoneNumber}</FormLabel>
                <QuestionMark 
                  tabIndex="0" 
                  name="phoneNumberTooltip" 
                  id="phoneNumberTooltip"
                  onFocus={toggleTooltip}>
                  <QuestionMarkIcon />
                </QuestionMark>
                <Popover
                  placement="top"
                  isOpen={isTooltipOpen}
                  target="phoneNumberTooltip"
                  toggle={toggleTooltip}
                  trigger="hover"
                  aria-describedby="tooltipBody"
                >
                  <PopoverBody
                    id="tooltipBody"
                    aria-live="polite"
                    role="alert"
                  >
                    {formLabels.phoneNumberTooltip}
                  </PopoverBody>
                </Popover>
                <Popover
                  placement="top"
                  isOpen={isTooltipOpen}
                  target="phoneNumberTooltip"
                  toggle={toggleTooltip}
                  trigger="focus"
                  aria-describedby="tooltipBody"
                >
                  <PopoverBody
                    id="tooltipBody"
                    aria-live="polite"
                    role="alert"
                  >
                    {formLabels.phoneNumberTooltip}
                  </PopoverBody>
                </Popover>
                <Input
                  innerRef={register}
                  id="phoneNumber"
                  name="phoneNumber"
                  invalid={!!errors.phoneNumber}
                  valid={
                    formState.touched.phoneNumber &&
                    !errors.phoneNumber &&
                    !!getValues()['phoneNumber']
                  }
                  placeholder="(___) ___-____"
                  disabled={submitting}
                  onChange={e => formatPhone(e, 'phoneNumber', false)}
                />
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="phoneNumber">{errors.phoneNumber && errors.phoneNumber.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup>
                <FormLabel for="subject">{formLabels.subject}</FormLabel>
                <Input
                  required
                  type="select"
                  innerRef={register({
                    required: formLabels.subjectErrorRequired,
                  })}
                  invalid={!!errors.subject}
                  valid={formState.touched.subject && !errors.subject}
                  id="subject"
                  name="subject"
                  disabled={submitting}
                  onChange={() => {
                    setValue('questionValue', '');
                    clearError('questionValue');
                  }}
                >
                  <option value="">{formLabels.subjectPlaceholder}</option>
                  {questions &&
                    questions.map(q => (
                      <option value={q.subject} key={q.subject}>
                        {q.subject}
                      </option>
                    ))}
                </Input>
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="subject">{errors.subject && errors.subject.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup>
                <FormLabel for="questionValue">{formLabels.question}</FormLabel>
                <Input
                  required
                  innerRef={register({
                    required: formLabels.questionErrorRequired,
                  })}
                  type="select"
                  id="questionValue"
                  name="questionValue"
                  invalid={!!errors.questionValue}
                  valid={formState.dirtyFields.has('questionValue') && !errors.questionValue}
                  disabled={!formState.dirtyFields.has('subject') || submitting}
                >
                  <option value="">{formLabels.questionPlaceholder}</option>
                  {watch('subject') && getTopics()}
                </Input>
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="questionValue">{errors.questionValue && errors.questionValue.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <FormGroup>
                <FormLabel for="message">{formLabels.message}
                  <CharacterLimitAlert>
                    The maximum character limit for this text field is {characterLimit} characters.
                  </CharacterLimitAlert>
                </FormLabel>
                <MessageInput
                  required
                  innerRef={register({
                    required: formLabels.messageErrorRequired,
                  })}
                  id="message"
                  name="message"
                  type="textarea"
                  maxLength={characterLimit}
                  rows="4"
                  onChange={(event) => {
                    setCounter(event.target.value.length);
                    if ((event.target.value.length) === characterLimit) {
                      setLimitReached(true);
                    } else {
                      setLimitReached(false);
                    }
                  }}
                  placeholder={formLabels.messagePlaceholder}
                  invalid={!!errors.message}
                  valid={formState.touched.message && !errors.message}
                  disabled={submitting}
                />
                <CharacterCounter
                  data-testid="counter"
                  id="characterCounter"
                >
                  {counter}/{characterLimit} characters
                </CharacterCounter>
                <AlertPopup 
                  aria-live="polite" 
                  role="alert">
                  <FormSpan className={limitReached ? '' : 'active'}>Maximum character count reached. No more character inputs allowed.</FormSpan>
                </AlertPopup>
                <AlertPopup
                  aria-live="polite"
                  role="alert">
                  <FormFeedback aria-describedby="message">{errors.message && errors.message.message}</FormFeedback>
                </AlertPopup>
              </FormGroup>
            </Col>
            <Col sm={12}>
              <Actions>
                <Button
                  type="submit"
                  className="px-3 py-2"
                  disabled={!formState.isValid || submitting}
                >
                  {!submitting ? (
                    formLabels.submitButton
                  ) : (
                    <Spinner style={{ height: '21px', width: '21px' }} />
                  )}
                </Button>
              </Actions>
              <Link dangerouslySetInnerHTML={createSafeHtml(contactUslink)} />
            </Col>
          </Row>
        </Form>
      </PageContainer>
    </Row>
  );
};

export default SendUsMessage; 